/**
 * @llanddev/spanb-grille-integration — point d'entrée.
 *
 *   SpanbGrilleIntegration.create(document.querySelector('#maille'), {
 *     couches: { grille: 'https://…/grille/grille.json', regions: '…', sites: '…' },
 *   }).then((selecteur) => selecteur.on('change', (maille) => …))
 */
import 'ol/ol.css'
import './style.css'
import { Selecteur } from './selecteur'
import type { Config } from './types'

export { Selecteur }
export type { Config, CoucheSites, Evenements, FondDeCarte, Maille, SiteResume, SourceSelection, Theme, TypeDeSite, Valeur } from './types'
export type { Textes } from './textes'
export { FR as textesFr, EN as textesEn } from './textes'

export const version: string = __VERSION__

// Le nom n'est pas un littéral dans `new URL(…, import.meta.url)` : Vite le
// prendrait pour une ressource à embarquer. Ici, c'est un fichier voisin du script.
const NOM_CSS = 'spanb-grille-integration.css'
const chargements = new Map<string, Promise<void>>()

/**
 * Charge la feuille de style une fois par page, EN TÊTE du <head> : les règles du
 * site, qui viennent après, l'emportent à spécificité égale. Résolue au chargement
 * (ou au bout de 3 s), pour ne pas afficher le champ sans style.
 */
function chargerCss(css: Config['css']): Promise<void> {
  if (css === false) return Promise.resolve()
  const href = typeof css === 'string' ? new URL(css, document.baseURI).href : new URL(NOM_CSS, import.meta.url).href
  let p = chargements.get(href)
  if (!p) {
    p = new Promise<void>((resolve) => {
      const lien = document.createElement('link')
      lien.rel = 'stylesheet'
      lien.href = href
      lien.setAttribute('data-sgi-css', version)
      const fin = () => resolve()
      lien.addEventListener('load', fin, { once: true })
      lien.addEventListener('error', fin, { once: true })
      setTimeout(fin, 3000)
      document.head.prepend(lien)
    })
    chargements.set(href, p)
  }
  return p
}

/**
 * Crée un sélecteur sur un <input> (ou dans un conteneur, où un champ caché est
 * ajouté sous le nom `config.nom`). Rejette si la grille est introuvable.
 */
export async function create(element: HTMLElement | null, config: Config): Promise<Selecteur> {
  if (!element) throw new TypeError('create() : élément introuvable')
  await chargerCss(config?.css)
  return Selecteur.create(element, config)
}

/** Crée un sélecteur sur chaque élément qui correspond au sélecteur CSS, avec la même configuration. */
export async function createAll(selecteurCss: string, config: Config, racine: ParentNode = document): Promise<Selecteur[]> {
  return Promise.all(Array.from(racine.querySelectorAll<HTMLElement>(selecteurCss)).map((e) => create(e, config)))
}
